<template>
    <!-- Mismo diseño que Editar perfil y Datos bancarios (bkCuenta.css): lateral
         con título y avance vertical, tarjetas a la derecha y barra de guardado
         fija abajo. El avance acá son los requisitos de la contraseña. -->
    <div class="bk" :style="{ '--bk-cookies': cookiesAlto + 'px' }">
        <!-- ── Lateral: título, texto y la navegación de la cuenta ── -->
        <aside class="bk-side">
            <h1 class="bk-title">
                {{ $t('cambiar_password.title_a') }} <span class="bk-grad">{{ $t('cambiar_password.title_b') }}</span>
            </h1>
            <p class="bk-subtitle">{{ $t('cambiar_password.subtitle') }}</p>
            <CuentaNav />
        </aside>

        <div class="bk-main">
            <!-- Sin <form>: un envío nativo recarga la página y corta el pedido a
                 la mitad, y un form.submit() hecho por código ajeno (la extensión
                 de un gestor de contraseñas) ni siquiera emite evento para poder
                 frenarlo. El Enter dentro de un campo lo maneja este contenedor. -->
            <div class="bk-form" @keydown.enter="alEnter">
                <!-- Para los gestores de contraseñas: a qué cuenta pertenece. -->
                <input class="bk-sr-only" type="text" name="username" autocomplete="username" :value="correo" tabindex="-1" aria-hidden="true" readonly />

                <!-- 1 · Contraseña actual -->
                <section class="bk-card" :class="{ 'is-done': !errores.actual && !!form.actual }" aria-labelledby="cp-s1">
                    <header class="bk-card-head">
                        <span class="bk-step" aria-hidden="true">{{ !errores.actual && form.actual ? '✓' : '1' }}</span>
                        <div>
                            <h2 id="cp-s1" class="bk-card-title">{{ $t('cambiar_password.s1.title') }}</h2>
                            <p class="bk-card-desc">{{ $t('cambiar_password.s1.desc') }}</p>
                        </div>
                    </header>

                    <BkField id="cp-actual" v-model="form.actual" :type="ver.actual ? 'text' : 'password'"
                        :label="$t('cambiar_password.f.current')" autocomplete="current-password" :maxlength="100"
                        :error="errorDe('actual')" @input="actualIncorrecta = false" @blur="tocar('actual')">
                        <template #accion>
                            <button type="button" :class="['bk-eye', { 'is-on': ver.actual }]"
                                :aria-label="ver.actual ? $t('cambiar_password.hide') : $t('cambiar_password.show')"
                                :title="ver.actual ? $t('cambiar_password.hide') : $t('cambiar_password.show')"
                                :aria-pressed="String(ver.actual)" @click="ver.actual = !ver.actual">
                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12z" />
                                    <circle cx="12" cy="12" r="3.1" />
                                    <path v-if="ver.actual" d="M4 20 L20 4" />
                                </svg>
                            </button>
                        </template>
                    </BkField>
                </section>

                <!-- 2 · Contraseña nueva -->
                <section class="bk-card" :class="{ 'is-done': nuevaLista }" aria-labelledby="cp-s2">
                    <header class="bk-card-head">
                        <span class="bk-step" aria-hidden="true">{{ nuevaLista ? '✓' : '2' }}</span>
                        <div>
                            <h2 id="cp-s2" class="bk-card-title">{{ $t('cambiar_password.s2.title') }}</h2>
                            <p class="bk-card-desc">{{ $t('cambiar_password.s2.desc') }}</p>
                        </div>
                    </header>

                    <!-- Un solo ojo para la nueva y su repetición: son la misma contraseña. -->
                    <BkField id="cp-nueva" v-model="form.nueva" :type="ver.nueva ? 'text' : 'password'"
                        :label="$t('cambiar_password.f.new')" autocomplete="new-password" :maxlength="100"
                        :error="errorDe('nueva')" @blur="tocar('nueva')">
                        <template #accion>
                            <button type="button" :class="['bk-eye', { 'is-on': ver.nueva }]"
                                :aria-label="ver.nueva ? $t('cambiar_password.hide') : $t('cambiar_password.show')"
                                :title="ver.nueva ? $t('cambiar_password.hide') : $t('cambiar_password.show')"
                                :aria-pressed="String(ver.nueva)" @click="ver.nueva = !ver.nueva">
                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12z" />
                                    <circle cx="12" cy="12" r="3.1" />
                                    <path v-if="ver.nueva" d="M4 20 L20 4" />
                                </svg>
                            </button>
                        </template>
                    </BkField>

                    <!-- Requisitos en vivo (los mismos que pide el resto del sitio). -->
                    <ul class="bk-rules" :aria-label="$t('cambiar_password.rules_label')">
                        <li v-for="r in reglas" :key="r.clave" :class="{ 'is-ok': r.ok }">
                            <span class="bk-rule-mark" aria-hidden="true">{{ r.ok ? '✓' : '' }}</span>
                            {{ $t(`cambiar_password.rules.${r.clave}`) }}
                            <span class="bk-sr-only">{{ r.ok ? $t('cambiar_password.rule_ok') : $t('cambiar_password.rule_pending') }}</span>
                        </li>
                    </ul>

                    <BkField id="cp-repetir" v-model="form.repetir" :type="ver.nueva ? 'text' : 'password'"
                        :label="$t('cambiar_password.f.repeat')" autocomplete="new-password" :maxlength="100"
                        :error="errorDe('repetir')" :ok="iguales" @blur="tocar('repetir')" />
                </section>

                <!-- ── Barra de guardado (fija abajo) ── -->
                <div class="bk-bar">
                    <transition name="bk-toast">
                        <div
                            v-if="aviso"
                            :key="aviso.id"
                            :class="['bk-toast', `bk-toast--${aviso.tipo}`]"
                            :role="aviso.tipo === 'error' ? 'alert' : 'status'"
                            :aria-live="aviso.tipo === 'error' ? 'assertive' : 'polite'"
                        >
                            <span class="bk-toast-icon" aria-hidden="true">{{ aviso.tipo === 'ok' ? '✓' : '!' }}</span>
                            <p class="bk-toast-text">{{ aviso.texto }}</p>
                            <button v-if="aviso.reintentar" type="button" class="bk-toast-retry" @click="aviso.reintentar">
                                {{ $t('cambiar_password.toast.retry') }}
                            </button>
                            <button type="button" class="bk-toast-close" :aria-label="$t('cambiar_password.toast.close')" @click="cerrarAviso">×</button>
                        </div>
                    </transition>
                    <p class="bk-bar-state" :class="{ 'is-dirty': hayCambios, 'is-idle': !hayCambios && !actualizada }" aria-live="polite">
                        <span class="bk-bar-dot" aria-hidden="true"></span>
                        {{ estadoTexto }}
                    </p>
                    <div class="bk-bar-actions">
                        <button v-if="hayCambios" type="button" class="bk-btn bk-btn--ghost" :disabled="guardando" @click="descartar">
                            {{ $t('cambiar_password.discard') }}
                        </button>
                        <button type="button" class="bk-btn bk-btn--primary" :disabled="guardando || !hayCambios" @click="guardar">
                            <span v-if="guardando" class="bk-spinner" aria-hidden="true"></span>
                            {{ guardando ? $t('cambiar_password.saving') : $t('cambiar_password.save') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import BkField from "@/components/BankInformation/BkField.vue";
import cuentaFormMixin from "@/components/BankInformation/cuentaFormMixin";
import CuentaNav from "@/components/Cuenta/CuentaNav.vue";

// Las mismas reglas que pide el resto del sitio (registro, recuperar clave:
// pages/Login/CambiarClave.vue). La API no las valida: acepta cualquier cosa
// mientras la actual sea correcta y las dos nuevas coincidan.
const REGLAS = [
    { clave: 'length', test: v => v.length >= 6 && v.length <= 10 },
    { clave: 'upper', test: v => /[A-Z]/.test(v) },
    { clave: 'lower', test: v => /[a-z]/.test(v) },
    { clave: 'number', test: v => /[0-9]/.test(v) },
];
// Orden en que se enfoca el primer campo con error.
const CAMPOS = ['actual', 'nueva', 'repetir'];

export default {
    name: "CambiarPassword",
    components: { BkField, CuentaNav },
    mixins: [cuentaFormMixin],
    data() {
        return {
            form: { actual: '', nueva: '', repetir: '' },
            ver: { actual: false, nueva: false },
            correo: (JSON.parse(localStorage.getItem('usuario') || '{}') || {}).correo || '',
            // El mixin lo pide (la barra existe desde el principio: no hay carga).
            cargando: false,
            guardando: false,
            intentoGuardar: false,
            tocados: {},
            // La API contestó 406: la actual no es la de la cuenta.
            actualIncorrecta: false,
            // Recién guardada (hasta que se vuelva a escribir): estado de la barra.
            actualizada: false,
        };
    },
    computed: {
        reglas() {
            return REGLAS.map(r => ({ clave: r.clave, ok: r.test(this.form.nueva) }));
        },
        reglasOk() {
            return this.reglas.every(r => r.ok);
        },
        iguales() {
            return this.form.repetir.length > 0 && this.form.nueva === this.form.repetir;
        },
        nuevaLista() {
            return this.reglasOk && this.iguales && !this.errores.nueva;
        },
        errores() {
            const f = this.form;
            const e = {};
            if (!f.actual) e.actual = this.$t('validations.required');
            else if (this.actualIncorrecta) e.actual = this.$t('cambiar_password.err.wrong_current');

            if (!f.nueva) e.nueva = this.$t('validations.required');
            else if (!this.reglasOk) e.nueva = this.$t('cambiar_password.err.rules');
            else if (f.nueva === f.actual) e.nueva = this.$t('cambiar_password.err.same_as_current');

            if (!f.repetir) e.repetir = this.$t('validations.required');
            else if (f.repetir !== f.nueva) e.repetir = this.$t('cambiar_password.err.mismatch');
            return e;
        },
        // Lo usa el mixin (aviso del navegador al cerrar con algo escrito).
        hayCambios() {
            return CAMPOS.some(c => this.form[c].length > 0);
        },
        estadoTexto() {
            if (this.hayCambios) return this.$t('cambiar_password.state_unsaved');
            return this.actualizada ? this.$t('cambiar_password.state_saved') : this.$t('cambiar_password.state_idle');
        },
    },
    watch: {
        // "Revisa los campos marcados en rojo" se va solo al corregirlos.
        errores(e) {
            if (!Object.keys(e).length && this.aviso && this.aviso.validacion) this.cerrarAviso();
        },
        hayCambios(v) {
            if (v) this.actualizada = false;
        },
    },
    methods: {
        ...mapActions(["actualizarPassword"]),

        tocar(campo) {
            this.$set(this.tocados, campo, true);
        },
        // El error se muestra al salir del campo o al intentar guardar. La
        // contraseña incorrecta (respuesta de la API) se muestra siempre.
        errorDe(campo) {
            if (campo === 'actual' && this.actualIncorrecta) return this.errores.actual || '';
            return (this.tocados[campo] || this.intentoGuardar) && this.errores[campo] ? this.errores[campo] : '';
        },
        enfocar(campo) {
            const el = document.getElementById(`cp-${campo}`);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => el.focus({ preventScroll: true }), 350);
        },

        // Enter dentro de un campo = guardar. En los botones (el ojo, Descartar)
        // el Enter hace lo de siempre: apretar ese botón.
        alEnter(e) {
            if (e.target && e.target.tagName === 'INPUT') {
                e.preventDefault();
                if (!this.guardando && this.hayCambios) this.guardar();
            }
        },

        async guardar() {
            if (this.guardando) return;
            this.intentoGuardar = true;
            const conError = CAMPOS.find(c => this.errores[c]);
            if (conError) {
                this.mostrarAviso('error', this.$t('cambiar_password.toast.invalid'), null, { validacion: true });
                this.$nextTick(() => this.enfocar(conError));
                return;
            }

            this.guardando = true;
            try {
                await this.actualizarPassword({
                    contrasena: this.form.actual,
                    contrasenan: this.form.nueva,
                    contrasenar: this.form.repetir,
                });
                this.limpiar();
                this.actualizada = true;
                this.mostrarAviso('ok', this.$t('cambiar_password.toast.saved'));
            } catch (error) {
                const status = error && error.response ? error.response.status : 0;
                if (status === 406) {
                    // Lo único que hay que corregir es la actual: se marca ahí.
                    this.actualIncorrecta = true;
                    this.mostrarAviso('error', this.$t('cambiar_password.err.wrong_current'), null, { validacion: true });
                    this.$nextTick(() => this.enfocar('actual'));
                } else {
                    console.error('[CambiarPassword] users/changePassword falló', status, error && error.response && error.response.data);
                    const texto = status ? this.$t('cambiar_password.toast.error_save') : this.$t('cambiar_password.toast.offline');
                    this.mostrarAviso('error', texto, () => { this.cerrarAviso(); this.guardar(); });
                }
            } finally {
                this.guardando = false;
            }
        },

        limpiar() {
            this.form = { actual: '', nueva: '', repetir: '' };
            this.ver = { actual: false, nueva: false };
            this.intentoGuardar = false;
            this.tocados = {};
            this.actualIncorrecta = false;
        },
        descartar() {
            this.limpiar();
            this.cerrarAviso();
        },
    },
    created() {
        window.scrollTo(0, 0);
    },
};
</script>

<!-- Estilos compartidos con Editar perfil y Datos bancarios (todo con prefijo bk-). -->
<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="../../components/Cuenta/cuentaMenu.css"></style>
